/**
 * Resumable Upload Helper using TUS Protocol
 * For large files (>40 MB) to Supabase Storage
 */

import * as tus from 'tus-js-client';
import { supabase } from './supabase';

export interface UploadProgress {
  bytesUploaded: number;
  bytesTotal: number;
  percentage: number;
}

export interface ResumableUploadOptions {
  bucketName: string;
  filePath: string;
  file: File;
  onProgress?: (progress: UploadProgress) => void;
  onError?: (error: Error) => void;
}

/**
 * Upload a file using TUS resumable upload protocol
 * Works for files up to 5 GB (or your configured bucket limit)
 */
export async function resumableUpload(
  options: ResumableUploadOptions
): Promise<{ success: boolean; error?: Error }> {
  const { bucketName, filePath, file, onProgress, onError } = options;

  console.log('🚀 DEBUG: resumableUpload function called!');
  console.log('   Bucket:', bucketName);
  console.log('   File path:', filePath);
  console.log('   File size:', file.size, 'bytes');

  return new Promise(async (resolve) => {
    try {
      // Get current session for authorization
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        const error = new Error('No active session - user must be logged in');
        onError?.(error);
        resolve({ success: false, error });
        return;
      }

      // Get project reference from Supabase client
      const projectUrl = supabase.storage.from(bucketName).getPublicUrl('').data.publicUrl;
      const projectRef = new URL(projectUrl).hostname.split('.')[0];
      
      // Construct TUS endpoint URL
      const uploadUrl = `https://${projectRef}.supabase.co/storage/v1/upload/resumable`;

      console.log(`🚀 Starting TUS resumable upload for: ${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB)`);

      // Create TUS upload
      const upload = new tus.Upload(file, {
        endpoint: uploadUrl,
        retryDelays: [0, 3000, 5000, 10000, 20000], // Retry on failure
        headers: {
          authorization: `Bearer ${session.access_token}`,
          'x-upsert': 'false', // Set to true if you want to overwrite existing files
        },
        uploadDataDuringCreation: true,
        removeFingerprintOnSuccess: true, // Don't resume successful uploads
        metadata: {
          bucketName: bucketName,
          objectName: filePath,
          contentType: file.type,
          cacheControl: '3600',
        },
        chunkSize: 6 * 1024 * 1024, // 6 MB chunks (Supabase default)
        onError: function (error) {
          console.error('❌ TUS upload failed:', error);
          onError?.(error);
          resolve({ success: false, error });
        },
        onProgress: function (bytesUploaded, bytesTotal) {
          const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(1);
          console.log(`📤 Upload progress: ${percentage}% (${bytesUploaded}/${bytesTotal} bytes)`);
          
          onProgress?.({
            bytesUploaded,
            bytesTotal,
            percentage: parseFloat(percentage),
          });
        },
        onSuccess: function () {
          console.log(`✅ TUS upload completed: ${file.name}`);
          resolve({ success: true });
        },
      });

      // Check if there are any previous uploads to continue
      upload.findPreviousUploads().then(function (previousUploads) {
        // Found previous uploads so we select the first one
        if (previousUploads.length) {
          console.log('⏯️ Resuming previous upload...');
          upload.resumeFromPreviousUpload(previousUploads[0]);
        }

        // Start the upload
        upload.start();
      });
    } catch (error) {
      console.error('❌ Resumable upload setup failed:', error);
      const err = error instanceof Error ? error : new Error(String(error));
      onError?.(err);
      resolve({ success: false, error: err });
    }
  });
}

/**
 * Determine if a file should use resumable upload
 */
export function shouldUseResumableUpload(fileSize: number): boolean {
  const THRESHOLD = 40 * 1024 * 1024; // 40 MB
  return fileSize > THRESHOLD;
}
